import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEnglishUi, languageNameFor, useUiLanguage } from '@/features/chat-trf/language';
import { ViewOriginalModal } from './ViewOriginalModal';
import type { ChatMessage } from '@/types/api';
import { parseTranslationMeta } from '@/lib/translation-utils';
import { api } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { serverMessage } from '@/lib/serverMessage';


// Role-aware bubble chrome, matching the plain-bubble palette used on the
// handoff threads (ThreadParticipationPage / TrferHandoffMobilePage) so a
// translated message is visually indistinguishable from a non-translated one.
// `.chat-bubble`/`.content` carry no CSS of their own — they're kept only as
// stable hooks for tests + QA selectors; the utilities below do the styling.
function roleBubbleClasses(role: string | undefined): string {
  if (role === 'trfer') return 'bg-stevens-indigo-solid text-stevens-indigo-solid-fg';
  if (role === 'assistant') return 'bg-stevens-indigo/10 text-foreground border border-stevens-indigo/30';
  if (role === 'user') return 'bg-status-warn text-foreground border border-status-warn-border';
  return 'bg-muted text-foreground';
}

interface Props {
  message: ChatMessage;
  viewerLanguage: string;
  handoffId: string;
  /** The conversation_id for the thread — forwarded to ViewOriginalModal for the report payload. */
  conversationId: string;
}

export function TranslatedMessageBubble({ message, viewerLanguage, handoffId, conversationId }: Props) {
  const { t } = useTranslation('chat');
  const uiLanguage = useUiLanguage();
  const [showOriginal, setShowOriginal] = useState(false);
  const { toast } = useToast();
  const meta = parseTranslationMeta(message.metadata);

  const originalLang = meta?.original_language;
  const isTranslated = meta && originalLang && originalLang !== viewerLanguage;
  const translatedText = isTranslated ? meta?.translations?.[viewerLanguage as 'en' | 'es' | 'zh' | 'fil' | 'pt' | 'fr'] : null;
  const hasError = meta?.translation_error;

  // Prefer translated text; fall back to original when translation is missing.
  const displayText = translatedText || message.content;
  const showFallbackNotice = isTranslated && !translatedText && hasError;

  return (
    <div
      className={cn(
        'chat-bubble w-fit max-w-[80%] rounded-2xl px-3 py-2 text-body',
        roleBubbleClasses(message.role),
      )}
    >
      <div className="content whitespace-pre-wrap break-words">{displayText}</div>
      {isTranslated && translatedText && (
        <button
          type="button"
          className="block text-caption italic opacity-70 mt-1 text-left"
          aria-label={t('handoff.translation.viewOriginal')}
          onClick={() => setShowOriginal(true)}
        >
          {t('handoff.translation.translatedFrom', { language: languageNameFor(originalLang, uiLanguage) })}
        </button>
      )}
      {showFallbackNotice && (
        <div className="text-caption italic text-status-warn-fg mt-1 flex items-center gap-2">
          <span>{t('handoff.translation.unavailable')}</span>
          <button
            type="button"
            className="underline text-stevens-ink-3"
            onClick={async () => {
              try {
                await api.handoffs.retranslateMessage(handoffId, message.id);
                // SSE `message_updated` event will refresh this bubble once
                // the retranslation completes (wired in T14).
              } catch (err) {
                toast({
                  variant: 'destructive',
                  title: t('handoff.translation.retryFailed'),
                  description:
                    isEnglishUi(uiLanguage)
                      ? serverMessage(err, t('handoff.translation.retryFailedBody'))
                      : t('handoff.translation.retryFailedBody'),
                });
              }
            }}
          >
            {t('handoff.translation.retry')}
          </button>
        </div>
      )}
      {showOriginal && (
        <ViewOriginalModal
          originalText={message.content}
          originalLanguage={originalLang!}
          translatedText={translatedText || ''}
          targetLanguage={viewerLanguage}
          conversationId={conversationId}
          messageId={message.id}
          onClose={() => setShowOriginal(false)}
        />
      )}
    </div>
  );
}
